// src/ui/BrandLottie.jsx — the animated chat mark.
// Replaces the old "A Allo" text brand. Two Lottie files, one per theme
// (src/assets/lottie/chat-{dark,light}.json), switched live when the OS theme
// flips — same prefers-color-scheme convention the rest of the app uses.
//
// ── WHEN IT PLAYS IS A PROP, BECAUSE IT DEPENDS ON WHERE IT IS ───────────────
// 🟢 `trigger` says what STARTS a play, and the right answer differs per surface:
//
//      'mount'   (default)  the chat list, the login gate — the draw-on is a
//                           greeting, once per launch.
//      'visible'            the site footer, which is below the fold: playing on
//                           mount would mean it always finished before anyone
//                           could see it, so it waits for the viewport.
//      'click'               the site header, which is on screen from the first
//                           paint: it shows the FINISHED mark and only redraws
//                           when someone asks it to.
//
// 🟥 'mount' MUST STAY THE DEFAULT AND MUST STAY UNCHANGED. Six places render
//    this component; a new default would silently restyle all of them.

import React, { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import chatDark from '../assets/lottie/chat-dark.json';
import chatLight from '../assets/lottie/chat-light.json';

export default function BrandLottie({
  size = 128,
  bare = false,
  still = false,
  trigger = 'mount',
  /** accessible name — only meaningful when this mark is the ONLY thing naming its
   *  container. Where an ancestor already carries an aria-label, leave it off. */
  label,
}) {
  const [dark, setDark] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const cb = (e) => setDark(e.matches);
    // addEventListener with a fallback for older Safari
    if (mq.addEventListener) mq.addEventListener('change', cb);
    else mq.addListener(cb);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', cb);
      else mq.removeListener(cb);
    };
  }, []);

  /* 🟥 A MARK THAT REDRAWS WHEN YOU SCROLL PAST IT IS EXACTLY THE MOTION
     prefers-reduced-motion EXISTS FOR — site.css already honours it for the header
     border, and a scroll-triggered animation is a far stronger case than a 200ms
     border fade. When it is set, every trigger below simply parks on the finished
     mark: the logo is still there, it just does not draw itself.

     🟢 Only the NEW triggers are affected. 'mount' is what the six existing surfaces
     use and is left exactly as it was — quietly restyling all of them is not this
     change's business. */
  const [reduce, setReduce] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cb = (e) => setReduce(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', cb);
    else mq.addListener(cb);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', cb);
      else mq.removeListener(cb);
    };
  }, []);

  const player = useRef(null);
  const box = useRef(null);
  /* 🟢 ONCE MEANS ONCE FOR THE WHOLE VISIT, not once per scroll. Scrolling the
     footer out and back in is not a new greeting, it is the same person still
     reading the page — an animation that redraws every time they scroll past is
     the kind of movement people describe as "the site is glitching". */
  const played = useRef(false);
  /* The observer can fire before the animation data has loaded, in which case
     goToAndPlay has nothing to play. Remember the intent and honour it on load. */
  const pending = useRef(false);

  /* 🟢 `still` = THE MARK, NOT THE ANIMATION. In the chat list the draw-on is a
     greeting, once per launch. In a toolbar that a page re-renders all day it
     would be a twitch, so parking puts it on its LAST frame — the finished
     logo — rather than frame 0, which for a draw-on animation is a blank
     canvas. Same file, same two themes, no second asset to keep in step. */
  const quiet = reduce && trigger !== 'mount';
  const parked = still || trigger === 'click' || quiet;

  const parkNow = () => {
    const a = player.current;
    if (!a) return;
    try {
      const frames = a.getDuration(true) || 1;
      a.goToAndStop(Math.max(0, frames - 1), true);
    } catch { /* an older player: autoplay={false} already left it on frame 0 */ }
  };

  const playOnce = () => {
    const a = player.current;
    if (!a) { pending.current = true; return; }
    // One guard, and every path that plays goes through here: the observer, the
    // deferred play after load, and the click.
    if (quiet) { parkNow(); return; }
    try { a.goToAndPlay(0, true); } catch { /* nothing sensible to fall back to */ }
  };

  const onReady = () => {
    if (pending.current) { pending.current = false; playOnce(); return; }
    /* 🟥 A THEME FLIP REMOUNTS THE PLAYER — swapping animationData is what makes the
       mark follow the OS, and it also throws away the frame we were parked on. Without
       re-parking here, a footer mark that had already played would go blank the moment
       someone switched their system to dark, and a header mark would too. */
    if (parked || (trigger === 'visible' && played.current)) parkNow();
  };

  useEffect(() => {
    if (trigger !== 'visible') return undefined;
    const el = box.current;
    if (!el) return undefined;

    const fire = () => {
      if (played.current) return;
      played.current = true;
      playOnce();
    };

    // No IntersectionObserver (very old Safari): play on mount rather than never.
    if (typeof IntersectionObserver === 'undefined') { fire(); return undefined; }

    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { fire(); io.disconnect(); return; }
      }
    }, { threshold: 0.35 });   // enough of it on screen to be worth watching
    io.observe(el);
    return () => io.disconnect();
    // `quiet` is in the deps because playOnce closes over it: someone turning on
    // reduce-motion before scrolling to the footer must get the parked mark, not the
    // decision this effect made when it first ran.
  }, [trigger, quiet]);

  const art = (
    <Lottie
      lottieRef={player}
      onDOMLoaded={onReady}
      onDataReady={onReady}
      animationData={dark ? chatDark : chatLight}
      loop={false}
      autoplay={trigger === 'mount' && !still}
      // 🟢 `bare` = no negative margins, no nudge. Those exist only to tuck the mark into
      //    the sidebar header without changing its layout height; anywhere else they'd
      //    just clip the animation against whatever is above and below it.
      style={bare
        ? { height: size, width: size }
        : { height: size, width: size, margin: '-44px 0', transform: 'translateY(16px)' }}
      // margins stay SYMMETRIC (they set the element's layout height — making the
      // top margin less negative grew the element and shoved the whole chat list
      // down, which was the reported bug). The push-down is a transform instead:
      // translateY moves only the PIXELS, never the layout, so the header and the
      // list below keep their exact height.
    />
  );

  // 🟢 The default path renders exactly what it always did — no wrapper, no extra
  //    element in the six places that use it. The wrapper exists only for the two
  //    triggers that need a DOM node to observe or to click.
  if (trigger === 'mount') return art;

  return (
    <span
      ref={box}
      className="brand-lottie"
      onClick={trigger === 'click' ? playOnce : undefined}
      style={{ display: 'inline-flex', flex: 'none', lineHeight: 0 }}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': 'true' })}
    >
      {art}
    </span>
  );
}
